import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

/**
 * Error Boundary – zachytí chyby v renderu child komponent a zobrazí fallback UI.
 * Nepokrývá async chyby (např. v useEffect / fetch); ty je třeba ošetřit lokálně.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState((state) => ({ ...state, errorInfo }));
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (typeof this.props.onGoHome === 'function') {
      this.props.onGoHome();
    }
  };

  render() {
    if (this.state.hasError && this.state.error) {
      const isDev = process.env.NODE_ENV !== 'production';
      return (
        <div className="min-h-[40vh] flex items-center justify-center p-4">
          <Card className="max-w-md w-full shadow-elevated border-destructive/30">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-destructive-light">
                  <AlertTriangle className="w-8 h-8 text-destructive" aria-hidden />
                </div>
                <div>
                  <CardTitle className="text-xl">Něco se pokazilo</CardTitle>
                  <CardDescription>
                    Došlo k neočekávané chybě. Zkuste obnovit stránku nebo se vrátit na přehled.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <Button onClick={this.handleRetry} className="gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Zkusit znovu
                </Button>
                <Button variant="outline" onClick={this.handleGoHome} className="gap-2">
                  <Home className="w-4 h-4" />
                  Na přehled
                </Button>
              </div>
              {isDev && this.state.error?.message && (
                <details className="mt-4 p-3 rounded-lg bg-muted text-sm">
                  <summary className="cursor-pointer font-medium text-foreground">
                    Technické detaily (pouze vývoj)
                  </summary>
                  <pre className="mt-2 overflow-auto text-xs text-muted-foreground whitespace-pre-wrap break-words">
                    {this.state.error.message}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-2 overflow-auto text-xs text-muted-foreground whitespace-pre-wrap break-words">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
